import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ShieldCheckIcon,
  CalendarIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { publicAPI } from '../config/api';
import LoadingScreen from '../components/common/LoadingScreen';

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const currentLang = i18n.language || 'ar';

  useEffect(() => {
    loadPolicy();
  }, [currentLang]);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await publicAPI.getPrivacyPolicy(currentLang);
      setPolicy(response.data.data);
    } catch (error) {
      console.error('Error loading privacy policy:', error);
      setError(error.response?.data?.message || 'خطأ في تحميل سياسة الخصوصية');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">خطأ في التحميل</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadPolicy}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <ShieldCheckIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">سياسة الخصوصية غير متوفرة</h1>
          <p className="text-gray-600">
            سياسة الخصوصية غير متوفرة حالياً. يرجى المحاولة مرة أخرى لاحقاً.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {policy.title}
            </h1>
            <div className="flex items-center justify-center space-x-6 space-x-reverse text-sm text-gray-500">
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 ml-1" />
                آخر تحديث: {new Date(policy.lastUpdated).toLocaleDateString(currentLang === 'ar' ? 'ar-SA' : 'en-US')}
              </div>
              <div className="flex items-center">
                <GlobeAltIcon className="h-4 w-4 ml-1" />
                الإصدار: {policy.version}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="p-8 lg:p-12">
            <div 
              className="prose prose-lg max-w-none"
              style={{ 
                direction: currentLang === 'ar' ? 'rtl' : 'ltr',
                textAlign: currentLang === 'ar' ? 'right' : 'left'
              }}
            >
              <div 
                dangerouslySetInnerHTML={{ __html: policy.content.replace(/\n/g, '<br />') }}
                className="text-gray-700 leading-relaxed whitespace-pre-wrap"
              />
            </div>
          </div>
        </motion.div>

        {/* Footer note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 text-center"
        >
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <ShieldCheckIcon className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-blue-800">
              هذه السياسة سارية المفعول اعتباراً من تاريخ آخر تحديث المذكور أعلاه.
              للاستفسارات حول سياسة الخصوصية، يرجى التواصل معنا.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
